import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { Send, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import { TopBar } from "@/components/TopBar";
import { ChatInput, type ChatAttachment } from "@/components/ChatInput";
import ReactMarkdown from "react-markdown";
import { streamChat, fileToBase64, type ChatMessage, type ChatMessageContent, type UserContext } from "@/lib/chatClient";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/hooks/useAuth";
import { useProjects } from "@/hooks/useProjects";
import { logEvent } from "@/lib/events";
import { ChatActions } from "@/components/ChatActions";
import { MessageFeedback } from "@/components/MessageFeedback";
import { ChatDisclaimer } from "@/components/ChatDisclaimer";
import { InlinePoptavkaBox } from "@/components/InlinePoptavkaBox";
import { isFacadeServiceSlug } from "@/config/serviceCatalog";
import { getServiceModule } from "@/services/registry";
import type { ServiceModule } from "@/services/types";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  attachments?: ChatAttachment[];
  /** Hodnocení AI odpovědi uložené uživatelem: 1 = 👍, -1 = 👎 */
  feedbackRating?: -1 | 1;
  /** ID zprávy v DB (pro propojení s feedback a training samples) */
  serverMessageId?: string;
}

function stripLeadBlock(text: string) {
  return text.replace(/---LEAD_DATA---[\s\S]*?---END_LEAD---/g, "").trim();
}

/** Detect if assistant message contains a price estimate table */
function hasPriceTable(content: string): boolean {
  // Must have a markdown table (|...|...|) AND a price value (Kč/CZK)
  return /\|.*\|.*\|/.test(content) && /\d[\d\s]*(?:Kč|CZK)/i.test(content);
}

const HEADLINE_KEYS = [
  "index.headline1",
  "index.headline2",
  "index.headline3",
  "index.headline4",
  "index.headline5",
];

const Index = () => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const isLoggedInRef = useRef(isLoggedIn);
  useEffect(() => { isLoggedInRef.current = isLoggedIn; }, [isLoggedIn]);

  // Zruší otevřený stream při odchodu ze stránky
  useEffect(() => {
    return () => { streamAbortRef.current?.abort(); };
  }, []);
  const { sites, chats, createChat, deleteChat, assignChat, saveChatMessage, loadChatMessages, addSite, reload } = useProjects();

  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);

  // Keep sidebar open on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [guestInquiryIds, setGuestInquiryIds] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("guest_inquiry_ids") || "[]"); } catch { return []; }
  });
  const [inputValue, setInputValue] = useState("");
  
  // Use useState to initialize the ref properly
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem("guest_session_id");
    if (existing) return existing;
    const newId = crypto.randomUUID();
    sessionStorage.setItem("guest_session_id", newId);
    return newId;
  });
  const guestSessionId = useRef<string>(sessionId);
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const saved = sessionStorage.getItem("guest_chat_messages");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const activeChatIdRef = useRef<string | null>(null);
  const inquirySubmittingRef = useRef(false);
  const streamAbortRef = useRef<AbortController | null>(null);
  const [lastCreatedInquiryId, setLastCreatedInquiryId] = useState<string | null>(null);
  /** Track message IDs where an inline inquiry was already submitted */
  const [inlineInquiryMap, setInlineInquiryMap] = useState<Record<string, string>>({});
  /** Engine slug detected by detect-intent */
  const detectedEngineRef = useRef<string | null>(null);
  const [userProfile, setUserProfile] = useState<{ email?: string; phone?: string | null; full_name?: string | null }>({});
  const headline = useMemo(() => t(HEADLINE_KEYS[Math.floor(Math.random() * HEADLINE_KEYS.length)]), [t]);
  /** Service module loaded via registry (null = generic) */
  const serviceModuleRef = useRef<ServiceModule | null>(null);

  // Save guest chat to DB via edge function (fire-and-forget)
  const saveGuestChatToDb = useCallback(async (msgs: Message[]) => {
    if (isLoggedIn || msgs.length === 0) return;
    const sessionId = typeof guestSessionId.current === "function" 
      ? (guestSessionId.current as () => string)() 
      : guestSessionId.current;
    try {
      const serializable = msgs.map(({ role, content }) => ({ role, content: content.slice(0, 2000) }));
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/guest-inquiry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ action: "save_chat", session_id: sessionId, messages: serializable, lang }),
      });
    } catch (e) {
      console.error("Guest chat save error:", e);
    }
  }, [isLoggedIn]);

  // Persist guest chat messages to sessionStorage + DB
  useEffect(() => {
    if (!isLoggedIn && messages.length > 0) {
      const serializable = messages.map(({ id, role, content }) => ({ id, role, content }));
      sessionStorage.setItem("guest_chat_messages", JSON.stringify(serializable));
      saveGuestChatToDb(messages);
    }
    if (isLoggedIn) {
      sessionStorage.removeItem("guest_chat_messages");
    }
  }, [messages, isLoggedIn, saveGuestChatToDb]);


  useEffect(() => {
    if (!user) { setUserProfile({}); return; }
    const load = async () => {
      const { data } = await supabase.from("profiles").select("phone, full_name").eq("user_id", user.id).single();
      setUserProfile({ email: user.email, phone: data?.phone, full_name: data?.full_name });
    };
    load();
  }, [user]);

  const handleSelectChat = useCallback(async (chatId: string) => {
    setActiveChatId(chatId);
    activeChatIdRef.current = chatId;
    const dbMessages = await loadChatMessages(chatId);
    setMessages(
      dbMessages.map((m) => ({
        id: m.id,
        role: m.role as "user" | "assistant",
        content: m.content,
      }))
    );
  }, [loadChatMessages]);

  const handleNewChat = useCallback(() => {
    setActiveChatId(null);
    activeChatIdRef.current = null;
    setMessages([]);
    setInputValue("");
    sessionStorage.removeItem("guest_chat_messages");
  }, []);

  const handleDeleteChat = useCallback(async (chatId: string) => {
    await deleteChat(chatId);
    if (activeChatId === chatId) {
      handleNewChat();
    }
    toast.success(t("sidebar.chatDeleted"));
  }, [deleteChat, activeChatId, handleNewChat, t]);

  const handleAssignChat = useCallback(async (chatId: string, siteId: string | null) => {
    await assignChat(chatId, siteId);
    toast.success(t("sidebar.chatAssigned"));
  }, [assignChat, t]);

  const handleAddSite = useCallback(async (name: string) => {
    await addSite(name);
    toast.success(t("profile.siteAdded"));
  }, [addSite, t]);

  /**
   * Uloží hodnocení AI odpovědi do DB.
   * Přihlášení uživatelé: ukládá user_id + project_id.
   * Hosté: ukládá session_id.
   */
  const handleFeedback = useCallback(async (
    messageId: string,
    messageContent: string,
    rating: -1 | 1,
    correction?: string,
    precedingQuestion?: string,
    chatMessageId?: string,
  ) => {
    // Okamžitě uloží rating do lokálního stavu
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, feedbackRating: rating } : m))
    );

    try {
      await (supabase as any).from("message_feedback").insert({
        project_id: activeChatIdRef.current ?? null,
        session_id: isLoggedIn ? null : guestSessionId.current,
        user_id: user?.id ?? null,
        engine_id: detectedEngineRef.current || null,
        message_content: messageContent.slice(0, 3000),
        preceding_question: precedingQuestion ? precedingQuestion.slice(0, 1000) : null,
        rating,
        correction: correction ? correction.slice(0, 2000) : null,
        chat_message_id: chatMessageId ?? null,
      });
    } catch (e) {
      console.error("Failed to save message feedback:", e);
    }

  }, [isLoggedIn, user]);

  const handleSend = async (message: string, attachments?: ChatAttachment[]) => {
    // Zruší případný probíhající stream před novým odesláním
    streamAbortRef.current?.abort();
    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    const userMsg: Message = { id: Date.now().toString(), role: "user", content: message, attachments };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputValue("");
    setIsLoading(true);

    // Create or reuse chat project for logged-in users
    let chatId = activeChatIdRef.current;
    if (isLoggedIn && !chatId) {
      const chatName = message.slice(0, 60) || "Chat";
      chatId = await createChat(chatName, undefined, null);
      if (chatId) {
        setActiveChatId(chatId);
        activeChatIdRef.current = chatId;
      }
    }

    // Save user message
    if (chatId) {
      await saveChatMessage(chatId, "user", message);
    }

    // Hybrid service detection via backend (keywords + AI, with persistence)
    if (!detectedEngineRef.current && newMessages.filter(m => m.role === "user").length <= 1) {
      try {
        const { data } = await supabase.functions.invoke("detect-intent", {
          body: {
            query: message,
            source: "frontend",
            sessionId: guestSessionId.current || undefined,
            projectId: chatId || undefined,
            lang,
          },
        });
        if (data?.detection?.outcome === "matched" && data.detection.finalSlug) {
          detectedEngineRef.current = data.detection.finalSlug;
          const mod = await getServiceModule(data.detection.finalSlug);
          if (mod) serviceModuleRef.current = mod;
          // Update project with detected engine_id so it shows in chat history
          if (chatId) {
            supabase.from("projects").update({ engine_id: data.detection.finalSlug }).eq("id", chatId).then(() => {});
          }
          console.log(
            "Engine detected:", data.detection.finalSlug,
            "method:", data.detection.routingMethod,
            mod ? "(custom module)" : "(generic)"
          );
        } else {
          // outcome 'uncertain' or 'new_service_suggested' → generic flow, detection persisted on backend
          console.log("Service detection:", data?.detection?.outcome, data?.detection?.routingReason);
        }
      } catch (e) {
        console.error("Engine detection failed (non-blocking):", e);
      }
    }

    const chatHistory: ChatMessage[] = [];
    for (const m of newMessages) {
      if (m.role === "user" && m.attachments?.some((a) => a.type === "image")) {
        const contentParts: ChatMessageContent = [];
        if (m.content) {
          const imageAnalysisPrompt = isFacadeServiceSlug(detectedEngineRef.current)
            ? "Analyzuj tuto fotku domu a odhadni plochu fasády v m²."
            : "Analyzuj tuto fotku a popiš co vidíš.";
          contentParts.push({ type: "text", text: m.content || imageAnalysisPrompt });
        }
        for (const att of m.attachments.filter((a) => a.type === "image")) {
          const base64 = await fileToBase64(att.file);
          contentParts.push({ type: "image_url", image_url: { url: base64 } });
        }
        if (!m.content) {
          const imageAnalysisPrompt = isFacadeServiceSlug(detectedEngineRef.current)
            ? "Analyzuj tuto fotku domu a odhadni plochu fasády v m²."
            : "Analyzuj tuto fotku a popiš co vidíš.";
          contentParts.unshift({ type: "text", text: imageAnalysisPrompt });
        }
        chatHistory.push({ role: "user", content: contentParts });
      } else {
        chatHistory.push({ role: m.role, content: m.content });
      }
    }

    let assistantContent = "";
    const assistantId = (Date.now() + 1).toString();
    let assistantSaved = false;
    let savedMessageId: string | null = null;

    const userContext: UserContext = {
      isLoggedIn,
      email: userProfile.email,
      phone: userProfile.phone,
      fullName: userProfile.full_name,
    };

    const saveAssistantMessage = async (): Promise<string | null> => {
      if (assistantSaved) return savedMessageId;
      assistantSaved = true;
      const currentChatId = activeChatIdRef.current;
      if (currentChatId && assistantContent) {
        const displayContent = stripLeadBlock(assistantContent);
        const msgId = await saveChatMessage(currentChatId, "assistant", displayContent);
        if (msgId) savedMessageId = msgId;
        reload();
        return msgId;
      }
      return null;
    };

    const saveTrainingSample = async () => {
      const currentChatId = activeChatIdRef.current;
      const sessionIdVal = typeof guestSessionId.current === "function" ? (guestSessionId.current as () => string)() : guestSessionId.current;
      const msgsForTraining = newMessages
        .concat([{ id: assistantId, role: "assistant" as const, content: assistantContent }])
        .map((m) => ({ role: m.role, content: typeof m.content === "string" ? m.content : "" }));
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save-training-sample`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            messages: msgsForTraining,
            assistant_content: assistantContent,
            engine_id: detectedEngineRef.current || null,
            has_images: newMessages.some((m) => m.attachments?.some((a) => a.type === "image")),
            lang: lang || "cs",
            project_id: currentChatId || null,
            session_id: !isLoggedIn ? sessionIdVal : null,
            chat_message_id: savedMessageId || null,
            user_id: user?.id ?? null,
          }),
        });
      } catch (e) {
        console.error("save-training-sample failed:", e);
      }
    };

    await streamChat({
      messages: chatHistory,
      lang,
      engine: detectedEngineRef.current || null,
      userContext,
      signal: abortController.signal,
      onDelta: (chunk) => {
        assistantContent += chunk;
        const displayContent = stripLeadBlock(assistantContent);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && last.id === assistantId) {
            return prev.map((m) => (m.id === assistantId ? { ...m, content: displayContent } : m));
          }
          return [...prev, { id: assistantId, role: "assistant", content: displayContent }];
        });
      },
      onDone: async () => {
        setIsLoading(false);
        const msgId = await saveAssistantMessage();
        if (msgId) {
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, serverMessageId: msgId } : m)));
        }
        await saveTrainingSample();
      },
      onError: (err) => toast.error(err),
      onLeadData: async (data) => {
        console.log("Lead data received:", data);
        if (inquirySubmittingRef.current) {
          console.log("Inquiry already submitting, skipping duplicate");
          return;
        }
        inquirySubmittingRef.current = true;
        try {
          // Save assistant message BEFORE navigating away (onDone may not fire if we navigate)
          await saveAssistantMessage();

          // For guests: force-save the full chat to DB before creating inquiry
          if (!isLoggedInRef.current) {
            const currentMessages = [...messages, { id: assistantId, role: "assistant" as const, content: stripLeadBlock(assistantContent) }];
            await saveGuestChatToDb(currentMessages);
          }

          toast.success(t("index.leadSaved"));

          if (isLoggedIn && user) {
            try {
              const updates: Record<string, string> = {};
              if (data.phone && !userProfile.phone) updates.phone = String(data.phone);
              if (data.email && !userProfile.full_name && data.full_name) updates.full_name = String(data.full_name);
              if (Object.keys(updates).length > 0) {
                await supabase.from("profiles").update(updates).eq("user_id", user.id);
                setUserProfile(prev => ({ ...prev, ...updates }));
              }
            } catch (profileErr) {
              console.error("Profile update failed (non-blocking):", profileErr);
            }
          }

          const { data: { session } } = await supabase.auth.getSession();
          const authToken = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

          const resp = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-inquiry`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
              body: JSON.stringify({
                ...data,
                project_id: activeChatIdRef.current,
                engine_id: detectedEngineRef.current || null,
                lang,
              }),
            }
          );
          
          if (!resp.ok) {
            console.error("create-inquiry failed:", resp.status, await resp.text());
            return;
          }
          
          const result = await resp.json();
          console.log("Inquiry created successfully:", result.id, "isLoggedIn:", isLoggedIn);
          if (result.id) {
            setLastCreatedInquiryId(result.id);
            logEvent("inquiry_submitted", { inquiry_id: result.id });
          }
          
          if (result.id && !isLoggedIn) {
            setGuestInquiryIds(prev => {
              const updated = [...prev, result.id];
              localStorage.setItem("guest_inquiry_ids", JSON.stringify(updated));
              return updated;
            });
            // Save guest token for accessing inquiry detail
            if (result.token) {
              try {
                const tokens = JSON.parse(localStorage.getItem("guest_inquiry_tokens") || "{}");
                tokens[result.id] = result.token;
                localStorage.setItem("guest_inquiry_tokens", JSON.stringify(tokens));
              } catch {}
            }
          }
          if (result.emailSent) {
            toast.success("📧 Potvrzení odesláno na váš e-mail");
          }
          if (result.suppliersNotified) {
            console.log(`Inquiry created, ${result.suppliersNotified} suppliers notified`);
          }
          // Redirect both logged-in and guest users to inquiry detail
          if (result.id) {
            console.log("Redirecting to inquiry detail:", `/dashboard/${result.id}`);
            setTimeout(() => navigate(`/dashboard/${result.id}`), 1500);
          }
        } catch (err) {
          console.error("Failed to create inquiry:", err);
        } finally {
          inquirySubmittingRef.current = false;
        }
      },
    });
  };


  // Extract data from chat messages for direct inquiry creation
  const extractChatData = () => {
    const svcMod = serviceModuleRef.current;
    const chatMsgs = messages.map((m) => ({ role: m.role, content: m.content }));

    // If service module has custom extraction, use it
    if (svcMod?.extractChatData) {
      return svcMod.extractChatData(chatMsgs);
    }

    // Generic: extract basic price info from text
    const allText = messages.filter(m => m.role === "assistant").map(m => m.content).join(" ");
    const priceMatches = allText.match(/(\d[\d\s]*\d)\s*[–-]\s*(\d[\d\s]*\d)\s*Kč/);
    return {
      area_m2: null,
      facade_type: null,
      material: null,
      thickness: null,
      price_low: priceMatches ? parseInt(priceMatches[1].replace(/\s/g, "")) : null,
      price_high: priceMatches ? parseInt(priceMatches[2].replace(/\s/g, "")) : null,
    };
  };

  const handleDirectInquiry = async () => {
    if (inquirySubmittingRef.current) return;
    inquirySubmittingRef.current = true;
    setIsLoading(true);

    try {
      const chatData = extractChatData();
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-inquiry`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            ...chatData,
            email: userProfile.email,
            phone: userProfile.phone,
            project_id: activeChatIdRef.current,
            engine_id: detectedEngineRef.current || null,
            lang,
          }),
        }
      );

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        toast.error(errData.error || "Nepodařilo se vytvořit poptávku");
        return;
      }

      const result = await resp.json();
      toast.success(t("index.leadSaved"));
      if (result.id) {
        logEvent("inquiry_submitted", { inquiry_id: result.id });
        navigate(`/dashboard/${result.id}`);
      }
    } catch (err) {
      console.error("Direct inquiry error:", err);
      toast.error("Nepodařilo se vytvořit poptávku");
    } finally {
      inquirySubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  /** Handle inline inquiry box submission (called from InlinePoptavkaBox) */
  const handleInlineInquiry = async (msgId: string, email: string, phone: string): Promise<string | null> => {
    try {
      const chatData = extractChatData();
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      // For guests: save chat to DB before creating inquiry
      if (!isLoggedInRef.current) {
        await saveGuestChatToDb(messages);
      }

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-inquiry`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            ...chatData,
            email,
            phone: phone || null,
            project_id: activeChatIdRef.current,
            engine_id: detectedEngineRef.current || null,
            lang,
          }),
        }
      );

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || "Nepodařilo se vytvořit poptávku");
      }

      const result = await resp.json();
      if (result.id) {
        toast.success(t("index.leadSaved"));
        logEvent("inquiry_submitted", { inquiry_id: result.id, source: "inline_box" });
        setLastCreatedInquiryId(result.id);
        setInlineInquiryMap(prev => ({ ...prev, [msgId]: result.id }));

        // Update profile if logged in and new data
        if (isLoggedIn && user) {
          try {
            const updates: Record<string, string> = {};
            if (phone && !userProfile.phone) updates.phone = phone;
            if (Object.keys(updates).length > 0) {
              await supabase.from("profiles").update(updates).eq("user_id", user.id);
              setUserProfile(prev => ({ ...prev, ...updates }));
            }
          } catch { /* non-blocking */ }
        }

        // For guests: save token + inquiry ID
        if (!isLoggedIn) {
          setGuestInquiryIds(prev => {
            const updated = [...prev, result.id];
            localStorage.setItem("guest_inquiry_ids", JSON.stringify(updated));
            return updated;
          });
          if (result.token) {
            try {
              const tokens = JSON.parse(localStorage.getItem("guest_inquiry_tokens") || "{}");
              tokens[result.id] = result.token;
              localStorage.setItem("guest_inquiry_tokens", JSON.stringify(tokens));
            } catch { /* ignore */ }
          }
        }

        // Redirect to inquiry detail after short delay
        setTimeout(() => navigate(`/dashboard/${result.id}`), 1500);
        return result.id;
      }
      return null;
    } catch (err) {
      console.error("Inline inquiry error:", err);
      throw err;
    }
  };

  const handleHowItWorks = () => {
    const userMsg: Message = { id: Date.now().toString(), role: "user", content: t("sidebar.howToUseQuery") };
    const assistantMsg: Message = { id: (Date.now() + 1).toString(), role: "assistant", content: t("sidebar.howToUseResponse") };
    setMessages([userMsg, assistantMsg]);
    setActiveChatId(null);
    activeChatIdRef.current = null;
    if (window.innerWidth < 1024) setSidebarOpen(false);
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="flex h-[100dvh] bg-background overflow-hidden">
      <AppSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen((prev) => !prev)}
        onClose={() => setSidebarOpen(false)}
        onHowItWorks={handleHowItWorks}
        onAboutUs={() => {}}
        isLoggedIn={isLoggedIn}
        sites={sites}
        chats={chats}
        activeChatId={activeChatId}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        onDeleteChat={handleDeleteChat}
        onAssignChat={handleAssignChat}
        onAddSite={handleAddSite}
        guestInquiryIds={guestInquiryIds}
        guestChatMessageCount={!isLoggedIn ? messages.length : 0}
      />

      <div className={`flex-1 flex flex-col h-full overflow-hidden transition-all duration-200 ${sidebarOpen ? "lg:ml-[260px]" : ""}`}>
        <TopBar onMenuToggle={() => setSidebarOpen((prev) => !prev)} showAuth={!isLoggedIn} isLoggedIn={isLoggedIn} sidebarOpen={sidebarOpen} />

        {!hasMessages ? (
          <>
            <div className="flex-1 flex flex-col items-center justify-center px-4">
              <h1 className="text-xl sm:text-3xl md:text-[28px] font-semibold text-foreground text-center mb-6 animate-fade-in whitespace-nowrap">
                {headline}
              </h1>
              <div className="flex justify-center gap-2 mb-4">
                <button
                  onClick={handleHowItWorks}
                  className="px-3.5 py-1.5 text-xs text-muted-foreground bg-muted rounded-full hover:bg-muted/70 hover:text-foreground transition-colors"
                >
                  {t("sidebar.howToUse")}?
                </button>
              </div>
              <div className="w-full max-w-[680px]">
                <ChatInput
                  onSend={handleSend}
                  value={inputValue}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="pb-4 px-4">
              <ChatDisclaimer />
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto scrollbar-none">
              <div className="max-w-3xl mx-auto w-full px-4 py-6 space-y-6">
                {messages.map((msg, msgIndex) => (
                  <div key={msg.id} className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground"
                      }`}
                    >
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {msg.attachments.map((att) =>
                            att.type === "image" && att.previewUrl ? (
                              <img
                                key={att.id}
                                src={att.previewUrl}
                                alt={att.file.name}
                                className="max-w-[200px] max-h-[200px] rounded-lg object-cover"
                              />
                            ) : (
                              <div key={att.id} className="flex items-center gap-1.5 bg-background/20 rounded-lg px-2.5 py-1.5 text-xs">
                                <span className="truncate max-w-[120px]">{att.file.name}</span>
                              </div>
                            )
                          )}
                        </div>
                      )}
                      {msg.content && (
                        <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      )}
                    </div>

                    {/* Hodnocení – zobrazí se pouze pod dokončenými AI zprávami */}
                    {msg.role === "assistant" && !(isLoading && msgIndex === messages.length - 1) && !msg.feedbackRating && (
                      <MessageFeedback
                        messageId={msg.id}
                        onFeedback={async (rating, correction) => {
                          const preceding = messages.slice(0, msgIndex).reverse().find((m) => m.role === "user");
                          await handleFeedback(msg.id, msg.content, rating, correction, preceding?.content, msg.serverMessageId);
                        }}
                      />
                    )}
                    {/* Pokud uživatel zprávu již hodnotil, zobrazíme ikonku */}
                    {msg.role === "assistant" && msg.feedbackRating !== undefined && (
                      <div className={`mt-1.5 ml-1 text-xs ${msg.feedbackRating === 1 ? "text-green-600" : "text-red-500"}`}>
                        {msg.feedbackRating === 1 ? "👍" : "👎"}
                      </div>
                    )}
                    {/* Inline inquiry box – shown after assistant messages with price tables */}
                    {msg.role === "assistant" && !(isLoading && msgIndex === messages.length - 1) && hasPriceTable(msg.content) && (
                      <div className="w-full max-w-[80%]">
                        <InlinePoptavkaBox
                          defaultEmail={userProfile.email || user?.email}
                          defaultPhone={userProfile.phone || undefined}
                          inquiryId={inlineInquiryMap[msg.id] || (lastCreatedInquiryId && msgIndex === messages.length - 1 ? lastCreatedInquiryId : undefined)}
                          onSubmit={async (email, phone) => {
                            const id = await handleInlineInquiry(msg.id, email, phone);
                            return id;
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
                  <div className="flex justify-start">
                    <div className="bg-secondary text-secondary-foreground rounded-2xl px-4 py-3 text-sm">
                      <span className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    </div>
                  </div>
                )}
                <div ref={(el) => el?.scrollIntoView({ behavior: "smooth" })} />
              </div>
            </div>
            <div className="pb-4 pt-2">
              <div className="relative max-w-[680px] mx-auto">
                {/* CTA suggestion above input — only for non-table pricing or LEAD_DATA fallback */}
                {!isLoading && messages.length >= 2 && (() => {
                  const lastAssistant = [...messages].reverse().find(m => m.role === "assistant") as Message | undefined;
                  const hasPricing = lastAssistant && /\d[\d\s]*(?:Kč|CZK|kč)/i.test(lastAssistant.content);
                  const hasLead = lastAssistant && /LEAD_DATA|Hotovo|Poptávka vytvořena|přesměruju/i.test(lastAssistant.content);
                  const hasTable = lastAssistant && hasPriceTable(lastAssistant.content);

                  // For guests: show "Open inquiry" button after inquiry was created (via LEAD_DATA fallback)
                  if (!isLoggedIn && lastCreatedInquiryId && !hasTable) {
                    return (
                      <div className="mb-3 animate-fade-in">
                        <button
                          onClick={() => navigate(`/dashboard/${lastCreatedInquiryId}`)}
                          className="w-full group relative overflow-hidden rounded-xl border border-primary/40 bg-gradient-to-r from-primary/5 via-primary/15 to-primary/5 hover:from-primary/10 hover:via-primary/20 hover:to-primary/10 transition-all duration-300"
                        >
                          <div className="flex items-center gap-3 px-4 py-3">
                            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                              <svg className="w-4.5 h-4.5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect width="18" height="18" x="3" y="3" rx="2" />
                                <path d="M9 9h6" /><path d="M9 13h6" /><path d="M9 17h4" />
                              </svg>
                            </div>
                            <div className="flex-1 text-left">
                              <span className="text-sm font-semibold text-foreground">Poptávka vytvořena – otevřít detail</span>
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                {"V detailu můžeš doplnit fotky a další informace pro firmy."}
                              </p>
                            </div>
                            <svg className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                            </svg>
                          </div>
                        </button>
                      </div>
                    );
                  }

                  // Fallback CTA for pricing without table (inline box handles the table case)
                  if (hasPricing && !hasLead && !hasTable) {
                    return (
                       <div className="mb-3 animate-fade-in">
                        <button
                          onClick={() => isLoggedIn ? handleDirectInquiry() : handleSend("Chci odeslat poptávku k nezávaznému nacenění od firem z mého okolí.")}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-border hover:border-primary/30 hover:bg-primary/5 transition-all text-left group"
                        >
                          <Send className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="flex-1 text-sm font-medium text-foreground truncate">Odeslat poptávku k nacenění</span>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
                <ChatInput
                  onSend={handleSend}
                  value={inputValue}
                  disabled={isLoading}
                  placeholder={t("index.nextQuestion")}
                />
                {!isLoading && messages.length > 0 && (
                  <div className="absolute right-2 -top-8">
                    <ChatActions
                      chatId={activeChatId}
                      currentSiteId={chats.find((c) => c.id === activeChatId)?.construction_site_id}
                      sites={sites}
                      isLoggedIn={isLoggedIn}
                      onAssignChat={handleAssignChat}
                      onDeleteChat={activeChatId ? handleDeleteChat : () => handleNewChat()}
                    />
                  </div>
                )}
              </div>
              <div className="mt-2 px-4">
                <ChatDisclaimer />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Index;
